import { Location, PlatformLocation, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { DictionaryService, FocusService, genTinyGUID } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { LenderService } from '@shared/service/lender/lender.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { Info } from '@workflow/ecp-shared/constant/information.const';
import { ADDITIONAL_BENEFICIAL_COUNT, RESPONSIBLE_PARTIES, RESPONSIBLE_PARTIES_ISPARTY } from '@workflow/ecp-shared/constant/constants';
import { getPageIterationId, processPath } from '../utilities.service';
import { VisibleFunctionsService } from './visible-functions.service';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { ValueLabelItem } from '@workflow/class/enum-item.class';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { LogicFnRef, PrimitiveType } from '@workflow/class/workflow.type';
import { LogicFnContext, LogicService } from '@workflow/service/logic/logic.service';
// eslint-disable-next-line max-len
import { WorkflowPageNavigationStateService } from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { UIFieldMapping } from '../../component/building-block/dynamic-personal-info/dynamic-personal-info.const';
import { SlideOutContent } from '../../component/custom-pages/slideout-page/slideout-page.component';
import { DisclosureCategory } from '../../constant/disclosure-category.const';
import { PROTOCOL_MORTGAGE } from '../../product-helper';
import { Disclosure, DisclosureApplicant } from '../../types/disclosure.type';
import { JointCreditManager } from '../../types/joint-applicant/joint-credit-manager.class';
import { PersonalDynamic } from '../../types/personal-dynamic.type';
import { ApplicationApiService } from '../application-api/application-api.service';
import { L360Service } from '../l360/l360.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance, FieldService } from '@workflow/class/field.service';
import { CustomFunctionsService } from './custom-functions.service';
import { FlowService } from '@workflow/class/flow.service';
import { OaoPageHelper } from '../../../oao/helper/oao-page.helper';

const BUSINESS_RESPONSIBLE_ID = 'oao-business-responsible-parties-list';
const BUSINESS_ADDITIONAL_ID = 'oao-business-additional-beneficial-list';
const FIRSTNAME = 'firstName';
const LASTNAME = 'lastName';
const ISRESPONSIBLITY = 'isResponsibleParty';
const PERCENTAGE = 'percentage';
const OAO_MEMBERSHIPID_STORAGE_ID = 'api:oaoMemberInformation/membership/membershipID';
const OAO_DROPDOWN_MEMBERSHIPID_STORAGE_ID = 'ss:oaoMemberInformation/membership/dropdownMembershipID';
const OAO_MEMBERSHIP_MIN_AGE_STORAGE_ID = 'api:oaoMemberInformation/membership/minAge';
const OAO_MEMBERSHIP_MAX_AGE_STORAGE_ID = 'api:oaoMemberInformation/membership/maxAge';
const OAO_MEMBERSHIP_IS_YOUTH_STORAGE_ID = 'api:oaoMemberInformation/membership/isYouth';
const AUTOFILLOPTION = 'autoFillOption';

const OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_EMAIL = 'api:oaoMemberInformation/businessInformation/responsibleParties/0/email';
const OAO_PRIMARY_APPLICANT_EMAIL = 'api:oaoMemberInformation/primaryApplicant/email';
const OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_MOBILENUMBER =
  'api:oaoMemberInformation/businessInformation/responsibleParties/0/mobileNumber';
const OAO_PRIMARY_APPLICANT_MOBILENUMBER = 'api:oaoMemberInformation/primaryApplicant/mobileNumber';
const OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_FIRSTNAME = 'api:oaoMemberInformation/businessInformation/responsibleParties/0/firstName';
const OAO_PRIMARY_APPLICANT_FIRSTNAME = 'api:oaoMemberInformation/primaryApplicant/firstName';
const OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_LASTNAME = 'api:oaoMemberInformation/businessInformation/responsibleParties/0/lastName';
const OAO_PRIMARY_APPLICANT_LASTNAME = 'api:oaoMemberInformation/primaryApplicant/lastName';

const buildEnumItem = (value, label?) => {
  return {
    label: label || value,
    value
  } as ValueLabelItem;
};

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({
  providedIn: 'root'
})
export class OnChangeFunctionsService {
  private logger = inject(SawDust);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Used to prevent double calls.
   */
  private readonly _flipCache: { [key: string]: boolean } = {};
  /** boolean to show membership change confirmation modal */
  public showMembershipChangeModal: boolean;
  /** set membership context on membership change */
  public oaoMembershipSelectionContext: LogicFnContext;
  private readonly statefulService = inject(StatefulService);
  private readonly fieldService = inject(FieldService);

  constructor(
    private readonly visibleFunctionsService: VisibleFunctionsService,
    private readonly location: Location,
    private readonly platformLocation: PlatformLocation,
    public readonly applicationApiService: ApplicationApiService,
    public readonly l360ApiService: L360Service,
    public readonly dictionaryService: DictionaryService,
    public readonly userService: UserService,
    private readonly lenderService: LenderService,
    public readonly router: Router,
    private readonly bundleHelperService: BundleHelperService,
    public readonly workflowService: WorkflowService,
    private readonly pageStateService: WorkflowPageNavigationStateService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly customFunctionsService: CustomFunctionsService,
    private readonly flowService: FlowService,
    private readonly oaoPageHelper: OaoPageHelper,
    private readonly logicservice: LogicService,
    private readonly focusService: FocusService
  ) {}

  /**
   * OAO ONLY
   * validate and update array with given number of month
   * @param requiredMonth - minimum no of months
   * @param inputInYear - flag to decide input in month / year
   * @param relatedFieldId - any related that should be taken in to account
   * @param relativeInputInYear - flag to decide related input in month / year
   */
  public arrayAdjustOnMinimumMonth = (
    context: LogicFnContext,
    requiredMonth: number,
    inputInYear = false,
    relatedFieldId?: string,
    relativeInputInYear = false
  ): void => {
    const storageId = context.field.config.storageId;
    const relativeField = relatedFieldId
      ? this.fieldService.createField(relatedFieldId, context.field.flowId, context.field.iterationId, context.field.pageIterationId)
      : undefined;
    const isRelativefield = relativeField !== undefined;
    const arrayRoot = storageId.split('/$')[0].split('$')[0];
    const pageIterationId = getPageIterationId(context);
    const pageArrayRoot = processPath(arrayRoot, pageIterationId);
    const monthsPerYear = 12;
    const currentSize = this.statefulService.arraySize(arrayRoot, pageIterationId);
    const firstContainer = this.statefulService.wrap(storageId, 0, pageIterationId);
    const firstContainerSum = inputInYear ? firstContainer.valueNumberOnly * monthsPerYear : firstContainer.valueNumberOnly;
    let relativeFirstContainer;
    let relativeFirstContainerValue;

    if (this.statefulService.isArrayAdjusting(arrayRoot)) {
      return; // already adjusting
    }

    // avoid array adjustment when fields aren't required.
    if (!context.field.required && !relativeField?.required) {
      return;
    }

    let sum = 0;
    sum = sum + firstContainerSum;
    if (isRelativefield) {
      relativeFirstContainer = this.statefulService.wrap(relativeField.config.storageId, 0, pageIterationId);
      relativeFirstContainerValue = relativeInputInYear
        ? relativeFirstContainer.valueNumberOnly * monthsPerYear
        : relativeFirstContainer.valueNumberOnly;
      sum += relativeFirstContainerValue;
    }

    this.setAdjustOnMinMonthValue({
      currentSize,
      storageId,
      pageIterationId,
      requiredMonth,
      sum,
      pageArrayRoot,
      inputInYear,
      relativeField,
      relativeInputInYear
    });
  };

  private setAdjustOnMinMonthValue(data: {
    currentSize: number;
    storageId: string;
    pageIterationId: number;
    requiredMonth;
    sum: number;
    pageArrayRoot: string;
    inputInYear;
    relativeField: FieldInstance;
    relativeInputInYear: boolean;
  }) {
    const { currentSize, storageId, pageIterationId, requiredMonth, pageArrayRoot, inputInYear, relativeField, relativeInputInYear } = data;
    let { sum } = data;
    const isRelativefield = relativeField !== undefined;
    const monthsPerYear = 12;
    let container: StatefulValue;
    let relativeContainer: StatefulValue;
    let removalIteration = currentSize - 1;
    for (let i = 0; i < currentSize; i++) {
      container = this.statefulService.wrap(storageId, i, pageIterationId);
      removalIteration = this.removeArrayRootIterationValue(currentSize, i, pageArrayRoot, requiredMonth, removalIteration, sum);
      if (i !== 0) {
        sum += inputInYear ? container.valueNumberOnly * monthsPerYear : container.valueNumberOnly;
      }

      if (isRelativefield && i !== 0) {
        relativeContainer = this.statefulService.wrap(relativeField.config.storageId, i, pageIterationId);
        sum += relativeInputInYear ? relativeContainer.valueNumberOnly * monthsPerYear : relativeContainer.valueNumberOnly;
      }
    }
    this.addArrayRootIterationValue(sum, requiredMonth, container, isRelativefield, relativeContainer, pageArrayRoot);
  }

  private removeArrayRootIterationValue(
    currentSize: number,
    index: number,
    pageArrayRoot: string,
    requiredMonth: number,
    removalIteration: number,
    sum: number
  ) {
    if (sum >= +requiredMonth && index <= currentSize - 1) {
      if (removalIteration !== 0) {
        this.statefulService.arrayRemove(pageArrayRoot, removalIteration);
      }
      removalIteration = removalIteration - 1;
    }
    return removalIteration;
  }

  private addArrayRootIterationValue(
    sum: number,
    requiredMonth: number,
    container: StatefulValue,
    isRelativefield: boolean,
    relativeContainer: StatefulValue,
    pageArrayRoot: string
  ) {
    if (
      sum < +requiredMonth &&
      (!container || container.valueNumber || (isRelativefield && !relativeContainer) || relativeContainer.valueNumber)
    ) {
      this.statefulService.arrayAdd(pageArrayRoot);
    }
  }

  // OAO ONLY
  public adjustOAOEmploymentOnMinimumMonths = (
    context: LogicFnContext,
    isYearField: boolean,
    minimumMonths: number,
    employmentStatus: string = null,
    relatedFieldId: string = null,
    isRelatedFieldAYear = false
  ): void => {
    const pageIterationId = getPageIterationId(context);
    const arrayStorageId = context.field.config.storageId.split('/$')[0].split('$')[0];
    const pageArrayRoot = processPath(arrayStorageId, pageIterationId);

    if (this.statefulService.isArrayAdjusting(arrayStorageId)) {
      return;
    }

    let totalMonthSum = 0;
    let shouldAddToArray = false;
    let cardsNeeded = 1;
    const currentArraySize = this.statefulService.arraySize(arrayStorageId, pageIterationId);
    const minimumGeneratedCards = employmentStatus === 'UnEmployed' || employmentStatus === 'Retired' ? 2 : 1;

    for (let i = 0; i < currentArraySize; i++) {
      const currentFieldValue = this.statefulService.wrap(context.field.config.storageId, i, pageIterationId).valueNumberOnly;
      let currentMonthSum = isYearField ? currentFieldValue * 12 : currentFieldValue;
      if (relatedFieldId != null) {
        const relatedFieldStorageId = this.fieldService.createField(relatedFieldId, context.field.flowId, i, pageIterationId).config
          .storageId;
        const relatedFieldValue = this.statefulService.wrap(relatedFieldStorageId, i, pageIterationId).valueNumberOnly;
        currentMonthSum += isRelatedFieldAYear ? relatedFieldValue * 12 : relatedFieldValue;
      }
      totalMonthSum += currentMonthSum;
      if (i === currentArraySize - 1) {
        shouldAddToArray = currentMonthSum !== 0;
      }
      if (totalMonthSum < minimumMonths) {
        cardsNeeded++;
      }
    }
    this.handleArrays(currentArraySize, minimumGeneratedCards, pageArrayRoot, shouldAddToArray, totalMonthSum, cardsNeeded, minimumMonths);
  };

  // Moved logic from above method for cognitive complexity.
  private handleArrays(
    currentArraySize,
    minimumGeneratedCards,
    pageArrayRoot,
    shouldAddToArray,
    totalMonthSum,
    cardsNeeded,
    minimumMonths
  ) {
    if (currentArraySize < minimumGeneratedCards) {
      this.addOrRemoveFromArray(minimumGeneratedCards - currentArraySize, pageArrayRoot, true);
    } else {
      if (shouldAddToArray && totalMonthSum < minimumMonths) {
        this.addOrRemoveFromArray(1, pageArrayRoot, true);
      }
      if (currentArraySize > cardsNeeded && totalMonthSum >= minimumMonths && currentArraySize > minimumGeneratedCards) {
        const cardsToRemove = currentArraySize - (cardsNeeded > minimumGeneratedCards ? cardsNeeded : minimumGeneratedCards);
        this.addOrRemoveFromArray(cardsToRemove, pageArrayRoot, false);
      }
    }
  }

  // TODO: confirm with Jesse if this fn is used anywhere??
  private readonly addOrRemoveFromArray = (numberToAddOrRemove: number, arrayStorageId: string, shouldAddToArray): void => {
    if (numberToAddOrRemove <= 0) {
      return;
    }
    if (numberToAddOrRemove === 1) {
      if (shouldAddToArray) {
        this.statefulService.arrayAdd(arrayStorageId);
      } else {
        this.statefulService.arrayRemove(arrayStorageId);
      }
      return;
    }
    if (shouldAddToArray) {
      this.statefulService.arrayAdd(arrayStorageId, () => {
        this.addOrRemoveFromArray(numberToAddOrRemove - 1, arrayStorageId, shouldAddToArray);
      });
    } else {
      this.statefulService.arrayRemove(arrayStorageId, null, () => {
        this.addOrRemoveFromArray(numberToAddOrRemove - 1, arrayStorageId, shouldAddToArray);
      });
    }
  };

  /**
   * OAO ONLY
   *
   * Updating array field value based on first array field value
   * @param context page and iteration info
   * @param fieldId array field path
   */
  public arrayFieldUpdateBasedOnfirstArrayField = (context: LogicFnContext, fieldId: string): void => {
    const storageId = fieldId;
    const arrayRoot = storageId.split('/$')[0].split('$')[0];
    const pageIterationId = context.field.pageIterationId;
    const currentSize = this.statefulService.arraySize(arrayRoot, pageIterationId);

    let prevContainer: StatefulValue;
    const currentContainervalue: PrimitiveType = this.statefulService.wrap(storageId, 0, pageIterationId).value;

    for (let i = 0; i < currentSize; i++) {
      prevContainer = this.statefulService.wrap(storageId, i, pageIterationId);
      if (prevContainer.value === undefined && (currentContainervalue === true || currentContainervalue === false)) {
        prevContainer.value = currentContainervalue;
      }
    }
  };

  /**
   * OAO ONLY
   * Updating field value based on first array field value
   * @param context page and iteration info
   * @param fieldId array field path
   * @param updatePath updating field path
   */
  public updatingFieldValueBasedonArrayField = (context: LogicFnContext, fieldId: string, updatePath: string): void => {
    const storageId = fieldId;
    const arrayZeroIndexValue = this.statefulService.wrap(storageId, 0, context.field.pageIterationId).value;
    this.statefulService.wrap(updatePath, undefined, context.field.pageIterationId).value = arrayZeroIndexValue;
  };

  /**
   * Check other fields
   */
  public checkOthersIfChecked = (context: LogicFnContext, ...fieldIds: string[]): void => {
    if (context.field?.data.value) {
      for (const fieldId of fieldIds) {
        const field = this.fieldService.createField(processPath(fieldId, context.field.pageIterationId), context.field.flowId);
        if (field) {
          field.data.value = true;
        }
      }
    }
  };

  public setFirstFocusableInput = (context: LogicFnContext, parentElementId: string, timeout = 0): void => {
    this.focusService.setFirstFocusableInput(parentElementId, timeout);
  };

  public setFocus = (context: LogicFnContext, elementId: string, timeout = 0): void => {
    this.focusService.setFocus(elementId, timeout);
  };

  /**
   * Unchecked other fields
   */
  public uncheckOthersIfChecked = (context: LogicFnContext, ...fieldIds: string[]): void => {
    if (context.field?.data.value) {
      for (const fieldId of fieldIds) {
        const targetField = this.fieldService.createField(
          fieldId,
          context.field.flowId,
          context.field.iterationId,
          context.field.pageIterationId
        );
        if (targetField?.config && targetField.config.type === 'checkbox') {
          this.fieldService.createField(processPath(fieldId, context.field.pageIterationId), context.field.flowId).data.value = false;
        } else {
          this.fieldService.createField(processPath(fieldId, context.field.pageIterationId), context.field.flowId).data.value = null;
        }
      }
    }
  };

  /**
   *  Uncheck other fields when unchecked
   */
  public uncheckOthersIfUnChecked = (context: LogicFnContext, ...fieldIds: string[]): void => {
    if (context.field && !context.field.data.value) {
      for (const fieldId of fieldIds) {
        this.fieldService.createField(processPath(fieldId, context.field.pageIterationId), context.field.flowId).data.value = false;
      }
    }
  };

  /**
   * Add 1 item on specific option selected
   * If the filed value is equal to refOption, it will empty the array.
   * @param context field context
   * @param path storage path
   * @param refOption the reference option value to empty the array.
   * @param callBackLogicFn callbackfunction refs
   */
  public addFirstItemOnOptionSelectedWithCallback = (
    context: LogicFnContext,
    pathValue: string,
    refOption: PrimitiveType,
    callBackLogicFn?: LogicFnRef
  ): void => {
    const path = processPath(pathValue, context.field.pageIterationId);

    if (!this._flipCache[context.field.data.id] && context.field.data.value === refOption && this.statefulService.arraySize(path) === 0) {
      this._flipCache[context.field.data.id] = true;
      this.statefulService.arrayAdd(path, () => {
        if (callBackLogicFn) {
          this.logicservice.evaluate([callBackLogicFn], context);
        }
        this._flipCache[context.field.data.id] = false;
      });
    }
  };

  public addFirstItemOnOptionSelected = (
    context: LogicFnContext,
    pathValue: string,
    refOption: PrimitiveType
  ): void => {
    this.addFirstItemOnOptionSelectedWithCallback(context, pathValue, refOption);
  };

  /**
   * Add 1 item on specific option selected
   * If the filed value is equal to refOption, it will empty the array.
   * @param context field context
   * @param path storage path
   * @param yes value on true
   * @param no value on false
   */
  public arrayHasLengthSetValue = (context: LogicFnContext, path: string, yes: PrimitiveType, no: PrimitiveType): void => {
    context.field.data.value = this.statefulService.arraySize(path, context.field.pageIterationId) ? yes : no;
  };

  /**
   * Set Parent parent id of all applicant when there is no joint applicant
   */
  public removeAllJointApplicant = (context: LogicFnContext): void => {
    if (!context.field.data.value) {
      new JointCreditManager(this.statefulService, PROTOCOL_MORTGAGE).removeAllJointCredit();
    }
  };

  /**
   * load branch users
   * TODO: Does this need to be window['_config']. Possible to use appConfig?
   */
  public loadBranchUsers = (context: LogicFnContext, branchId?: string): void => {
    const branchUsersStateful = this.statefulService.wrap('mem:branch-users');
    branchUsersStateful.value = '[]';

    if (!this.isBrowser) {
      this.statefulService.wrap('mem:show-card-spinner').value = false;
      return; // SSR-safe: avoid window access & DOM-dependent work
    }

    this.statefulService.wrap('mem:show-card-spinner').value = true;

    /* Prevent loading the branchUser APi,
       if consecutive requests come in at the same time
       If the loan participant list already exists
       If the path is postapp or the path is null
     */
    branchId = !branchId ? '' : branchId;
    if ((window['_config']?.lender && !window['_config'].lender.isMortgageAvailable) || String(this.location.path()) === '/null') {
      return;
    }

    this.applicationApiService.branchUserList(Info.interviewer, branchId, (error, data) => {
      const branchUsers = [];
      if (!error) {
        let participants = '';
        data.forEach((element) => {
          branchUsers.push({
            firstName: element.firstName,
            lastName: element.lastName,
            userId: element.userId
          });
          const selectedRole =
            element.roles && element.roles.length > 0
              ? element.roles.indexOf(Info.loanOfficer) >= 0
                ? Info.loanOfficer
                : element.roles[0]
              : '';
          participants += `${element.userId}-${selectedRole}/`;
        });
        this.statefulService.wrap('mem:show-card-spinner').value = false;
        this.statefulService.wrap('mem:loan-participant-list').value = participants;
      } else {
        this.statefulService.wrap('mem:show-card-spinner').value = false;
      }
      branchUsersStateful.value = JSON.stringify(branchUsers);
    });
  };

  /** On Branches Changes */
  public onChangeOAOBranch = (context: LogicFnContext): void => {
    const branch = context.field.enum.find((i) => i.value === context.field.data.value);
    if (branch?.label) {
      this.statefulService.wrap('api:oaoMemberInformation/preferredBankBranch/branchName').value = branch.label;
    }
  };
  /** On referral source Changes */
  public onChangeReferralSource = (_context: LogicFnContext): void => {
    const source: any = _context.field.enum.find((i) => i.value.toString() === _context.field.data.value.toString());
    if (source?.label) {
      this.statefulService.wrap('api:oaoMemberInformation/referralSource/id').value = source.id;
    }
  };

  /**
   * On Load of Membership page changing continue text according to user login
   * @param context Current Field context
   */
  public membershipPageNextBtnTextChanges = (context: LogicFnContext): void => {
    if (context?.page?.config) {
      if (this.userService?.isAuthenticated) {
        context.page.config.next = 'Continue application';
      }
    }
  };

  public onChangeMembership = (context: LogicFnContext): void => {
    /** Set value in memory to stored value in API */
    this.setMembershipTypeToMemStorage(context);
    this.oaoMembershipSelectionContext = context;
  };

  public setMembershipTypeToMemStorage = (context: LogicFnContext): void => {
    const field = context.field;
    if (
      field.config.payload?.['dropdownLists'] &&
      (field.config.payload['dropdownLists'] as any).length > 0 &&
      context?.field?.data?.value
    ) {
      const memberships = JSON.parse(field.config.payload['dropdownLists'] as string);
      if (!memberships || memberships.length == 0) {
        return;
      }

      const member = memberships.filter((i) => i.value.toString() === context.field.data.value.toString());
      if (member?.length > 0) {
        // HACER: Why are we storing these in memory? We can grab most of these from admin config
        // this.adminPortalFeaturesService?.config?.oaoFeatures?.allowPromoCode;
        this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipID').value = member[0].value ? member[0].value : '';
        this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipType').value = member[0].enumId ? member[0].enumId : null;
        this.statefulService.wrap('mem:oaoMemberInformation/membership/isYouth').value = member[0].isYouth;
        this.statefulService.wrap('mem:oaoMemberInformation/membership/description').value = member[0].description;
        this.statefulService.wrap('ss:oaoMemberInformation/membership/isEnabledChecklist').value = member[0].isEnabledChecklist;
        this.statefulService.wrap('ss:oaoMemberInformation/membership/checklistdescription').value = member[0].checklistDescription;
        this.statefulService.wrap('mem:oaoMemberInformation/membership/redirectURL').value = member[0].redirectURL;
        this.statefulService.wrap('mem:oaoMemberInformation/membership/isPromoCode').value = member[0].isPromoCode; //No longer need this

        const flow = this.flowService.createFlow(member[0].redirectURL);
        const eligiblity = flow.allPages.find((t) => t.id === 'oao-eligibility');
        this.statefulService.wrap('api:skipEligibility').value = !(eligiblity?.visible || false);

        // Set the promo code state
        // PROMO-TBD: We can remove this once maxPromoCodeDisplay is availiable in appInit for memberships
        if (member[0].isPromoCode) {
          const promoCodeCount = member[0]?.maxPromoCodeDisplay > 0 || member[0].isPromoCode ? 1 : 0;
          this.statefulService.wrap('api:oaoMemberInformation/membership/maxPromoCodeDisplay').value = promoCodeCount;
        }
      }
    }
  };

  /** Membership change patches */
  public patchMembership = (context: LogicFnContext): void => {
    const bundleMembershipId = this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipID').value;
    const dropdownMembershipId = this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value;
    const memberTypeID = bundleMembershipId || dropdownMembershipId;
    const field = context.field;

    if (field.config.payload?.dropdownLists && (field.config.payload?.dropdownLists as any).length > 0 && memberTypeID) {
      const memberships = JSON.parse(field.config.payload.dropdownLists as string);
      if (!memberships || memberships.length == 0) {
        return;
      }

      const member = memberships.filter((i) => i.value.toString() === memberTypeID.toString());
      if (member?.length > 0) {
        this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value = member[0].value ? member[0].value : '';
        this.statefulService.wrap('api:oaoMemberInformation/membership/minAge').value = this.getMinMaxAge(member[0].minAge);
        this.statefulService.wrap('api:oaoMemberInformation/membership/maxAge').value = this.getMinMaxAge(member[0].maxAge);
        this.statefulService.wrap('api:oaoMemberInformation/membership/membershipName').value = member[0].label ? member[0].label : '';
        this.statefulService.wrap('api:oaoMemberInformation/membership/redirectURL').value = member[0].redirectURL;
        this.statefulService.wrap('api:oaoMemberInformation/membership/membershipFee').value = member[0].membershipFee;
        this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').value = member[0].enumId
          ? member[0].enumId
          : null;
        this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').value = member[0].isYouth;
        this.statefulService.wrap(`api:oaoMemberInformation/hasCoApplicant`).value = member[0].isYouth ? Boolean(member[0].isYouth) : null;

        const flow = this.flowService.createFlow(member[0].redirectURL);
        const eligiblity = flow.allPages.find((t) => t.id === 'oao-eligibility');
        this.statefulService.wrap('api:skipEligibility').value = !(eligiblity?.visible || false);

        // Set the promo code state
        // PROMO-TBD: We can remove this once maxPromoCodeDisplay is availiable in appInit for memberships
        const promoCodeCount = member[0]?.maxPromoCodeDisplay > 0 || member[0].isPromoCode ? 1 : 0;
        this.statefulService.wrap('api:oaoMemberInformation/membership/maxPromoCodeDisplay').value = promoCodeCount;
      }
    }
  };

  private readonly getMinMaxAge = (value: string): number | string => {
    return value ?? null;
  };

  /** Clearing all OAO Application shares and Precise Id fields */
  public clearOAOApplicationFields = (): void => {
    const applicationId = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').value;
    if (applicationId) {
      /** Clearing Application,Applicant and Shares section */
      this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').value = null;
      this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/primaryApplicant/applicantId').value = null;
      this.statefulService.wrap('ss:oao-accounts-and-services').value = null;
      this.statefulService.wrap('mem:oao-accounts-and-services').value = null;
      this.statefulService.wrap('mem:accounts-and-services').value = null;
      this.statefulService.wrap('mem:oao-identity-verification-question').value = null;
      this.statefulService.arrayTruncate('api:oaoMemberInformation/accountsAndServices/primaryApplicant/shares');

      /** Clearing Precise ID section */
      this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value = null;
      this.statefulService.wrap('api:oaoMemberInformation/userIdentity/correlationId').value = null;
      this.statefulService.wrap('api:oaoMemberInformation/userIdentity/preciseIdFinalScore').value = null;
      this.statefulService.wrap('api:oaoMemberInformation/userIdentity/preciseIdFinalDecision').value = null;
      this.statefulService.wrap('api:oaoMemberInformation/isIdentityVerificationCompleted').value = null;
    }

    /** Clear Additional Questions */
    this.statefulService.arrayTruncate('api:oaoMemberInformation/customField/applicationCustomFields');

    // ReValidate All pages since membership get changed.
    this.statefulService.wrap('mem:ValidateAllPages').load();
  };

  /**
   * OAO ONLY
   * Remove additional co-applicant if co-app is empty
   */
  public removeAdditionalCoApp = (context: LogicFnContext): void => {
    const storageId = 'api:oaoMemberInformation/hasAdditionalCoApplicant';
    if (!context.field.data.value && storageId && this.statefulService.wrap(storageId).value) {
      this.statefulService.wrap(storageId).value = false;
    }
    if (!this.visibleFunctionsService.visibleIsOAOPrefillEnabled(context)) {
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillOption').value = 'Manual';
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/isPrefilled').value = false;
      this.statefulService.wrap('mem:primary-application-status-co-applicant').value = true;
    }
    this.l360ApiService.clearDisclosure();
  };

  /**
   * OAO ONLY
   * Remove both coApplicant and additionalCoApplicant
   */
  public removeCoApplicants = (context: LogicFnContext): void => {
    // Add logic to only remove coapplicants and additional coapplicants if they have data present
    // Check Additional CoApp fields for data
    const hasAdditionalCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/firstName').valueString ? true : false;
    //Check coapp fields for data
    const hasCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/coApplicant/firstName').valueString ? true : false;

    // Only remove additional coapp if there is data present
    if(hasAdditionalCoApplicant) {
      this.removeAdditionalCoApp(context);
    }
    // Only remove coapp if there is data present
    if(hasCoApplicant) {
      this.oaoPageHelper.deleteOaoApplicant('api:oaoMemberInformation/coApplicant');
    }
     // Needed to add this here. For some reason, this only existed in removeAdditionalCoApp,
     // So disclosures on review page was still there even after removing the coapp
      this.l360ApiService.clearDisclosure();
  };

  /**
   * Sets the storageId's value based on whether or not a checkbox is checked or unchecked.
   * @param context checkbox context
   * @param storageId stateful value to set.
   * @param newValue the value to set if the checkbox is checked.
   * @param falseValue the value to set if the checkbox is un-checked.
   */
  public setValueIfCheckboxClicked = (
    context: LogicFnContext,
    storageId: string,
    newValue: PrimitiveType,
    falseValue: PrimitiveType
  ): void => {
    const iterationId = 0;
    if (!context.field.data.value && !context.field.data.isChanged) {
      this.statefulService.wrap(storageId, iterationId).value = newValue;
    } else {
      this.statefulService.wrap(storageId, iterationId).value = falseValue;
    }
  };

  /**
   * Sets the storageId's value based on whether or not a checkbox is checked or unchecked for specific item based on iteration id.
   * @param context checkbox context
   * @param storageId stateful value to set.
   * @param newValue the value to set if the checkbox is checked.
   * @param falseValue the value to set if the checkbox is un-checked.
   */
  public setValueForCurrentIterationIfCheckboxClicked = (
    context: LogicFnContext,
    storageId: string,
    primaryApplicantStorageId: string,
    bussinfoPrimaryStorageId: string,
    falseValue: PrimitiveType
  ): void => {
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;

    if (membershipTypeName?.toLowerCase() === 'business') {
      this.statefulService.wrap(storageId, context.field.iterationId).value =
        !context.field.data.value && !context.field.data.isChanged ? this.statefulService.wrap(bussinfoPrimaryStorageId).value : falseValue;
    } else {
      this.statefulService.wrap(storageId, context.field.iterationId).value =
        !context.field.data.value && !context.field.data.isChanged
          ? this.statefulService.wrap(primaryApplicantStorageId).value
          : falseValue;
    }
  };

  public dynamicTitle = (context: LogicFnContext, prefix: string, ...values: string[]): string => {
    const jointValues = values ? values.join(' ') : '';
    const title = prefix ? prefix.replace('?', jointValues) : jointValues;
    const pageId = `${context.page.pageIterationId === undefined ? '' : context.page.pageIterationId + 1}`;
    const defaultTitle = context.page.config.title?.includes('#')
      ? `${context.page.config.title.replace('#', pageId)}`
      : `${context.page.config.title} ${pageId}`;
    return jointValues.trim() && title.trim() ? title : defaultTitle;
  };

  public dynamicReviewTitle = (context: LogicFnContext, reviewTitle: string): string => {
    const optionalReviewPages = context.page.hideOptionalReviewPages;
    if (optionalReviewPages) {
      return reviewTitle;
    }
    return context.page.config.title;
  };

  public textWhenHasData = (context: LogicFnContext, trueText: string, falseText?: string): string => {
    const isAlloyPrefillInProgress = this.statefulService.wrap('mem:alloy-prefill-inprogress').value as boolean;
    const isPrefilled =
      this.userService?.flowType?.toLowerCase() == 'oao'
        ? this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/isPrefilled').value
        : this.statefulService.wrap('ola:borrowerInfo/isPrefilled').value;
    const isStreamlined = this.flowService.createFlow(context.page?.flowId)?.config?.streamlined || false;
    if ((isPrefilled || this.platformLocation?.hash.length) && !isStreamlined && !isAlloyPrefillInProgress) {
      return trueText;
    }
    return falseText || null;
  };

  public dynamicLabel = (context: LogicFnContext, prefix: string, ...values: string[]): string => {
    const jointValues = values ? values.join(' ') : '';
    const label = prefix ? prefix.replace('?', jointValues) : jointValues;
    const defaultLabel = `${context.field.config.label}`;
    return jointValues.trim() && label.trim() ? label : defaultLabel;
  };

  public dynamicPageTitle = (context: LogicFnContext, prefix: string, hasCoApplicant: boolean, ...values: string[]): string => {
    const jointValues = values ? values.join(' ') : '';
    const title = prefix ? prefix.replace('?', jointValues) : jointValues;
    const defaultTitle = `${context.page.config.title}${
      context.page.pageIterationId === undefined ? '' : context.page.pageIterationId + 1
    }`;

    // Membership Type 1. Personal = 1, 2. Youth = 2, 3. Business = 3,
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    if (membershipTypeName?.toLowerCase() == 'business') {
      return jointValues.trim() && title.trim() ? title : defaultTitle;
    } else {
      hasCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
    }

    return jointValues.trim() && title.trim() && hasCoApplicant ? title : defaultTitle;
  };

  public dynamicCardTitle = (context: LogicFnContext, prefix: any, ...values: string[]): string => {
    const pageIterationId = getPageIterationId(context);
    const jointValues = this.resolveJointValues(values);
    const title = this.buildTitle(prefix, jointValues, context, pageIterationId);
    const defaultTitle = this.buildDefaultTitle(context, pageIterationId);
    return this.selectMembershipAwareTitle(jointValues, title, defaultTitle);
  };

  /** Resolve interpolation expressions and build a capitalized joint value string */
  private resolveJointValues(values: string[]): string {
    if (!values || values.length === 0) return '';
    const resolved = values.map((v) => this.resolveInterpolation(v));
    const capitalized = resolved.map((v) => (v ? v.replace(/^./, v[0].toUpperCase()) : ''));
    return capitalized.join(' ').trim();
  }

  private resolveInterpolation(raw: string): string {
    if (typeof raw !== 'string') return raw == null ? '' : String(raw);
    if (raw.startsWith('#{') && raw.endsWith('}')) {
      const path = raw.substring(2, raw.length - 1);
      try {
        const val = this.statefulService.wrap(path).value;
        return val == null ? '' : String(val);
      } catch {
        return '';
      }
    }
    return raw == null ? '' : String(raw);
  }

  private buildTitle(prefix: any, jointValues: string, context: LogicFnContext, pageIterationId: number): string {
    // Array style prefix handling
    if (Array.isArray(prefix) && prefix.length > 0) {
      const idx = prefix.length > context.card.iterationId ? context.card.iterationId : 1; // preserve legacy behavior
      let base = String(prefix[idx]);
      const usesArray = !!context.card.config.array;
      const size = usesArray ? this.statefulService.arraySize(context.card.config.array, pageIterationId) : 0;
      const addIterationSuffix = usesArray && size > 2 && context.card.iterationId >= 1;
      base = base ? base.replace('?', jointValues) : jointValues;
      return addIterationSuffix ? `${base} — ${context.card.iterationId}` : base;
    }
    // Simple string prefix
    if (typeof prefix === 'string') {
      return prefix.replace('?', jointValues);
    }
    return jointValues;
  }

  private buildDefaultTitle(context: LogicFnContext, pageIterationId: number): string {
    const cfgTitle = context.card.config.title;
    const iterationSuffix = context.card.pageIterationId === undefined ? '' : context.card.pageIterationId + 1;
    // Array form of title configuration
    if (Array.isArray(cfgTitle) && cfgTitle.length > 0) {
      const idx = cfgTitle.length > context.card.iterationId ? context.card.iterationId : 1;
      let base = String(cfgTitle[idx]);
      const usesArray = !!context.card.config.array;
      const size = usesArray ? this.statefulService.arraySize(context.card.config.array, pageIterationId) : 0;
      const addIterationSuffix = usesArray && size > 2 && context.card.iterationId >= 1;
      return addIterationSuffix ? `${base} — ${context.card.iterationId}` : base;
    }
    return `${cfgTitle}${iterationSuffix}`;
  }

  private selectMembershipAwareTitle(jointValues: string, title: string, defaultTitle: string): string {
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    const isBusiness = membershipTypeName?.toLowerCase() === 'business';
    if (isBusiness) {
      return jointValues && title.trim() ? title : defaultTitle;
    }
    const hasCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
    return jointValues && title.trim() && hasCoApplicant ? title : defaultTitle;
  }

  /**
   * conditionalTitle
   * JSON usage pattern (as introduced for primary applicant card):
   *   [
   *     "conditionalTitle",
   *     "#{api:oaoMemberInformation/primaryApplicant/isPrefilled}",
   *     "We were able to verify and pre-fill the following information",
   *     ["dynamicCardTitle", "?'s Information", "#{api:oaoMemberInformation/primaryApplicant/firstName}"]
   *   ]
   * @param context LogicFnContext supplied by logic engine
   * @param conditionRef string|boolean reference (may already be resolved by logic engine or be a raw storage ref expression)
   * @param trueTitle Title to use when condition resolves truthy
   * @param falseTitleOrArray Either a plain fallback title string OR an array logic ref (e.g. nested dynamicCardTitle invocation)
   */
  public conditionalTitle = (context: LogicFnContext, conditionRef: unknown, trueTitle: string, falseTitleOrArray: unknown): string => {
    let conditionValue: unknown = conditionRef;
    const isReview = this.oaoPageHelper.page.reviewPageIds?.includes(this.oaoPageHelper.page.id);
    const backFromReview = this.pageStateService.get(this.oaoPageHelper.page.id)?.isVisited;
    if (this.oaoPageHelper.page.path !== 'application-review' && !isReview && !backFromReview) {
      // If the conditionRef is a storage interpolation expression (starts with '#{'), resolve it
      if (typeof conditionRef === 'string' && conditionRef.startsWith('#{') && conditionRef.endsWith('}')) {
        const path = conditionRef.substring(2, conditionRef.length - 1);
        conditionValue = this.statefulService.wrap(path).value;
      }
      const isTruthy = !!conditionValue;
      if (isTruthy) {
        return trueTitle;
      }
    }
    // When fallback is an array we interpret it as another logic function invocation shape
    if (Array.isArray(falseTitleOrArray) && falseTitleOrArray.length > 0) {
      // Mirror evaluate semantics: [fnName, ...args]
      const [fnName, ...fnArgs] = falseTitleOrArray;
      const fn = (this as any)[fnName];
      if (typeof fn === 'function') {
        try {
          return fn.apply(this, [context, ...fnArgs]);
        } catch (e) {
          // swallow and fall back
        }
      }
    }
    return typeof falseTitleOrArray === 'string' ? falseTitleOrArray : '';
  };

  /** calculateDefaultAllocation() */
  public calculateDefaultAllocation = (): void => {
    this.oaoPageHelper.calculateDefaultAllocation();
  };

  /**
   * Load Responsible list for business
   *   * @param arrayPath - Path of respective business array
   */
  /** To load Responsible,Additional Reponsible and Authorized card List */
  public onChangeBusinessDropdownList = (context: LogicFnContext, arrayPath: string): void => {
    const selectedValue = this.contextCheck(context) ? context.field.data.valueNumber : 0;
    const prevArrayCount = this.statefulService.arraySize(arrayPath);
    if (selectedValue > prevArrayCount) {
      for (let i = prevArrayCount; i < selectedValue; i++) {
        this.statefulService.arrayAdd(arrayPath, () => {
          if (context.field.id.toLowerCase() != BUSINESS_RESPONSIBLE_ID && i != 0) {
            this.statefulService.wrap(`${arrayPath}/$/${FIRSTNAME}`, i).value = '';
            this.statefulService.wrap(`${arrayPath}/$/${LASTNAME}`, i).value = '';
            this.statefulService.wrap(`${arrayPath}/$/${ISRESPONSIBLITY}`, i).value = false;
            this.statefulService.wrap(`${arrayPath}/$/${PERCENTAGE}`, i).value = '';
          } else if (context.field.id.toLowerCase() == BUSINESS_RESPONSIBLE_ID && i == 0) {
            this.onChangeResponsiblePartiesDDL(context, arrayPath);
          }
          if (!this.visibleFunctionsService.visibleIsOAOPrefillEnabled(context)) {
            this.statefulService.wrap(`${arrayPath}/${i}/${AUTOFILLOPTION}`).value = 'Manual';
            this.statefulService.wrap(`mem:primary-application-status`).value = true;
          } else {
            this.statefulService.wrap(`${arrayPath}/${i}/${AUTOFILLOPTION}`).value = null;
            this.statefulService.wrap(`mem:primary-application-status`).value = null;
          }
          this.refreshNavigation();
        });
      }
    } else {
      for (let i = prevArrayCount - 1; i >= selectedValue; i--) {
        this.statefulService.arrayRemove(arrayPath, i, () => {
          if (i === selectedValue && context?.field?.id?.toLowerCase() === BUSINESS_RESPONSIBLE_ID) {
            this.loadAdditionalBeneficaryList(context, BUSINESS_ADDITIONAL_ID);
          }
          this.refreshNavigation();
        });
      }
    }
    this.l360ApiService.clearDisclosure();
  };

  /**
   * Load Additional Beneficary list for business
   *   * @param targetFieldId - ID of Enum
   */
  public loadAdditionalBeneficaryList = (context: LogicFnContext, targetFieldId: string, oaoPercentageFieldId?: string): void => {
    const targetField = this.fieldService.createField(targetFieldId, context.field.flowId);
    const responsibleCardSize = this.statefulService.arraySize(RESPONSIBLE_PARTIES);
    const maxCount = 4;
    if (!this.contextCheck(context) && oaoPercentageFieldId && context.field.iterationId) {
      this.statefulService.wrap(oaoPercentageFieldId, context.field.iterationId).value = null;
    }
    let selectedYesCount = 0;
    for (let i = 0; i < responsibleCardSize; i++) {
      if (this.statefulService.wrap(RESPONSIBLE_PARTIES_ISPARTY, i).value === true) {
        selectedYesCount++;
      }
    }
    const condition = maxCount - selectedYesCount;
    let enumvalue;
    while (targetField?.enum?.length > 0) {
      targetField.enum.pop();
    }
    const lastSelectedValue = this.statefulService.wrap(ADDITIONAL_BENEFICIAL_COUNT).valueNumber;
    if (condition > 0) {
      for (let i = 1; i <= condition; i++) {
        enumvalue = {
          enumId: i,
          label: i,
          value: i,
          virtual: false
        };
        targetField.enum.push(enumvalue);
      }
      if (lastSelectedValue > condition) {
        this.statefulService.wrap(ADDITIONAL_BENEFICIAL_COUNT).value = condition;
      }
    }
  };

  /** Method to Clear List for Business template */
  public clearBusinessListArray = (context: LogicFnContext, arrayPath: string, setEmptyValueStorageId: string): void => {
    const arraySize = this.statefulService.arraySize(arrayPath);
    if (!this.contextCheck(context) && arraySize > 0) {
      // this.statefulService.arrayTruncate(arrayPath);
      this.statefulService.wrap(setEmptyValueStorageId).value = '';
    }
  };

  private readonly refreshNavigation = (): void => {
    this.pageStateService.refreshPageState();
  };

  /** Method to remove Trailing Spaces */
  public removeTrailingSpaces = (context: LogicFnContext): void => {
    if (context.field?.data.value) {
      context.field.data.value = context.field.data.value.toString().trim();
    }
  };

  private readonly contextCheck = (context: LogicFnContext): boolean => {
    return !!context?.field?.data?.value;
  };

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.logger.logEvent(eventName, eventData);
  }

  /**
   * Load PickListItems
   *   * @param targetFieldId - ID of Enum
   */
  public loadPrimaryBusinessClassification = (context: LogicFnContext, businessClass: string): void => {
    const targetField = this.fieldService.createField(context.field.id, context.field.flowId);
    // Only call the API when the enum is not loaded. Once enum loaded here, then it will not make api call
    if (targetField?.enum?.length == 0) {
      const selectedValue = targetField.data.value;
      targetField.data.value = null;

      const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
      const logSrc = 'workflow';
      this.logEvent('ecp.ui.picklist', {
        eventState: 'start',
        correlationId: eventPairId,
        source: logSrc,
        keys: businessClass
      });

      const picklistTypes = this.l360ApiService.getPickListTypes(businessClass);
      if (picklistTypes?.length > 0) {
        let enumvalue;
        targetField.enum.length = 0;
        // tslint:disable-next-line: max-line-length
        picklistTypes[0].pickListItems.forEach((element) => {
          enumvalue = {
            enumId: element.id,
            label: element.value,
            value: element.id,
            virtual: false
          };
          targetField.enum.push(enumvalue);
        });

        setTimeout(() => {
          targetField.data.value = selectedValue === 0 ? null : selectedValue;
        }, 300);

        // REMOVE IN 218336
        this.logEvent('ecp.ui.picklist', {
          eventState: 'end',
          correlationId: eventPairId,
          source: logSrc
        });
      }
    }
  };

  /**
   * Load OAO Employment Status
   * @param targetFieldId - ID of Enum
   */
  public oaoReferralSourceEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const field = context.field;
    const membershipId = this.getMembershipId();

    if (!field.config.payload) {
      field.config.payload = {};
    }

    if (field.config.payload.membershipId != membershipId) {
      field.config.payload = {};
      field.config.payload.membershipId = membershipId;
    }

    if (
      field.config.payload.isLoading ||
      (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0)
    ) {
      const dropdownValues = field.config.payload['dropdownLists']
        ? (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
        : [];
      return dropdownValues;
    }
    field.config.payload.isLoading = true;

    const referralsources = this.adminPortalFeaturesService.config?.referralSources;
    if (referralsources) {
      const dropdownValues = referralsources;
      if (!dropdownValues) {
        return;
      }
      const list = Array.from(new Set(dropdownValues)).map((item: any) => {
        return { ...buildEnumItem(item.name), id: item.id };
      });
      field.config.payload['dropdownLists'] = JSON.stringify(list);
      field.config.payload.isLoading = false;
      return list;
    }

    return [];
  };

  /**
   * Load OAO Employment Status
   * @param targetFieldId - ID of Enum
   */
  public oaoPreferredBranchEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const field = context.field;
    const membershipId = this.getMembershipId();

    if (!field.config.payload) {
      field.config.payload = {};
    }

    if (field.config.payload.membershipId != membershipId) {
      field.config.payload = {};
      field.config.payload.membershipId = membershipId;
    }

    if (
      field.config.payload.isLoading ||
      (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0)
    ) {
      const dropdownValues = field.config.payload['dropdownLists']
        ? (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
        : [];
      return dropdownValues;
    }
    field.config.payload.isLoading = true;

    const preferredBankBranch = this.adminPortalFeaturesService.config?.preferredBranches;
    if (preferredBankBranch) {
      const dropdownValues = preferredBankBranch;
      if (!dropdownValues || dropdownValues.length <= 0) {
        this.statefulService.wrap('api:oaoMemberInformation/showPreferredbankbranches').value = false;
        return;
      }
      const list = Array.from(new Set(dropdownValues)).map((item: any) => buildEnumItem(item.id, item.name));
      field.config.payload['dropdownLists'] = JSON.stringify(list);
      this.statefulService.wrap('api:oaoMemberInformation/showPreferredbankbranches').value = true;
      field.config.payload.isLoading = false;
      return list;
    }

    return [];
  };

  /**
   * Load Organization Types
   * @param targetFieldId - ID of Enum
   */
  public oaoOrganizationTypesEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const field = context.field;

    if (!field.config.payload) {
      field.config.payload = {};
    }

    if (
      field.config.payload.isLoading ||
      (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0)
    ) {
      const dropdownValues = field.config.payload['dropdownLists']
        ? (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
        : [];
      return dropdownValues;
    }

    field.config.payload.isLoading = true;

    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    const logSrc = 'workflow';
    this.logEvent('ecp.ui.picklist', {
      eventState: 'start',
      correlationId: eventPairId,
      source: logSrc,
      keys: 'BusinessType'
    });

    const picklistTypes = this.l360ApiService.getPickListTypes('BusinessType');
    if (picklistTypes?.length > 0) {
      const dropdownValues = picklistTypes[0].pickListItems;
      if (!dropdownValues || dropdownValues.length <= 0) {
        return;
      }
      const list = Array.from(new Set(dropdownValues)).map((item: any) => buildEnumItem(item.id, item.value));
      field.config.payload['dropdownLists'] = JSON.stringify(list);
      field.config.payload.isLoading = false;

      this.logEvent('ecp.ui.picklist', {
        eventState: 'end',
        correlationId: eventPairId,
        source: logSrc,
        keys: 'BusinessType'
      });

      return list;
    }
    return [];
  };

  public loadSpecificBusinessClassification = (
    context: LogicFnContext,
    id: number,
    targetFieldId: string,
    dependentFieldId: string
  ): void => {
    const targetField = this.fieldService.createField(targetFieldId, context.field.flowId);
    const dependentField = this.fieldService.createField(dependentFieldId, context.field.flowId);
    const sourceField = this.fieldService.createField(context.field.id, context.field.flowId);

    id = sourceField && (!id || id == 0) ? sourceField.data.valueNumber : id;

    if (targetField && (!id || id == 0)) {
      targetField.enum.length = 0;
    }

    // Only call the API when the enum is not loaded. Once enum loaded here, then it will not make api call
    if (targetField && id > 0) {
      const selectedValue = targetField.data.value;
      targetField.data.value = null;

      this.l360ApiService.getSpecificBusinessClassification(id).subscribe(
        (response) => {
          if (response?.data?.length > 0) {
            let enumvalue;
            targetField.enum.length = 0;
            response.data.forEach((element) => {
              enumvalue = {
                enumId: element.id,
                label: element.value,
                value: element.id,
                virtual: false
              };
              targetField.enum.push(enumvalue);
            });

            if (context?.field?.dirty) {
              setTimeout(() => {
                if (!targetField.enum.some((enumItem) => enumItem.value === selectedValue || selectedValue === 0)) {
                  targetField.data.value = null;
                  dependentField.enum.length = 0;
                  dependentField.data.value = null;
                }
              }, 300);
            }
            if (context?.field?.dirty) {
              targetField.dirty = false;
              dependentField.dirty = false;
              dependentField.enum.length = 0;
              targetField.data.value = null;
              dependentField.data.value = null;
            }
            // TODO: Evaluate the ms here, and need for setTimeout in general
            setTimeout(() => {
              targetField.data.value = selectedValue === 0 ? null : selectedValue;
            }, 300);
          }
        },
        (err) => {}
      );
    }
  };

  public loadSpecificIndustryClassification = (context: LogicFnContext, id: number, targetFieldId: string): void => {
    const targetField = this.fieldService.createField(targetFieldId, context.field.flowId);

    if (targetField && (!id || id == 0)) {
      targetField.enum.length = 0;
    }
    // Only call the API when the enum is not loaded. Once enum loaded here, then it will not make api call
    if (targetField && id > 0) {
      const selectedValue = targetField.data.value;
      targetField.data.value = null;

      this.l360ApiService.getSpecificIndustryClassification(id).subscribe(
        (response) => {
          if (response?.data?.length > 0) {
            let enumvalue;
            targetField.enum.length = 0;
            response.data.forEach((element) => {
              enumvalue = {
                enumId: element.id,
                label: element.value,
                value: element.id,
                virtual: true
              };
              targetField.enum.push(enumvalue);
            });

            if (context?.field && !context.field.dirty) {
              setTimeout(() => {
                targetField.data.value = selectedValue;
                if (!targetField.enum.some((enumItem) => enumItem.value === selectedValue) || selectedValue === 0) {
                  targetField.data.value = null;
                  targetField.enum.length = 0;
                }
              }, 300);
            }

            if (context?.field?.dirty) {
              targetField.data.value = null;
              targetField.dirty = false;
            }
            // TODO: Evaluate the ms here, and need for setTimeout in general
            setTimeout(() => {
              targetField.data.value = selectedValue === 0 ? null : selectedValue;
            }, 300);
          }
        },
        (err) => {}
      );
    }
  };

  /**
   * Add/Remove 1 item on specific option selected
   * @param context field context
   * @param path storage path
   * @param shouldAddToArray the reference option value to true/false for Add/Remove.
   */
  public addorRemoveFromArray = (context: LogicFnContext, path: string, shouldAddToArray = true, maxAllowedLength: number = null): void => {
    if (!context.field.config.payload) {
      context.field.config.payload = {};
    }

    if (!context.field.config.payload.isLoading) {
      context.field.config.payload.isLoading = true;
      const pathPageId = path.indexOf('#') > -1 ? getPageIterationId(context) : -1;
      if (pathPageId >= 0) {
        const pageIterationId = getPageIterationId(context);
        const arrayRoot = path.split('/$')[0].split('$')[0];
        const pageArrayRoot = processPath(arrayRoot, pageIterationId);
        this.checkAndAddorRemove(context, pageArrayRoot, shouldAddToArray, maxAllowedLength);
      } else {
        this.checkAndAddorRemove(context, path, shouldAddToArray, maxAllowedLength);
      }
    }
    return;
  };

  /**
   * Add or Remove item into Array based on Max Allowed Length.
   * @param path
   * @param shouldAddToArray
   * @param maxAllowedLength
   */
  private checkAndAddorRemove(context: LogicFnContext, path: string, shouldAddToArray: boolean, maxAllowedLength: number) {
    if (shouldAddToArray) {
      if (maxAllowedLength) {
        const currentSize = this.statefulService.arraySize(path);
        if (maxAllowedLength > currentSize) {
          this.statefulService.arrayAdd(path, () => {
            context.field.config.payload.isLoading = false;
          });
        } else {
          context.field.config.payload.isLoading = false;
        }
      } else {
        this.statefulService.arrayAdd(path, () => {
          context.field.config.payload.isLoading = false;
        });
      }
    } else {
      this.statefulService.arrayRemove(path);
      context.field.config.payload.isLoading = false;
    }
  }

  public setValueBySelection = (context: LogicFnContext, storageId: string, value: PrimitiveType, falseValue: PrimitiveType): void => {
    if (context.field.data.value) {
      this.statefulService.wrap(storageId).value = value;
    } else {
      this.statefulService.wrap(storageId).value = falseValue;
    }
  };

  /**
   * Load OAO identification type
   * @param targetFieldId - ID of Enum
   */
  public oaoIdentificationTypesEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const field = context.field;
    let membershipId = this.getMembershipId();

    if (!field.config.payload) {
      field.config.payload = {};
    }

    if (field.config.payload.membershipId != membershipId) {
      field.config.payload = {};
      field.config.payload.membershipId = membershipId;
    }

    if (
      field.config.payload.isLoading ||
      (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0)
    ) {
      const dropdownValues = field.config.payload['dropdownLists']
        ? (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
        : [];
      this.onChangeIdentificationTypes(context);
      return dropdownValues;
    }
    if (!membershipId || membershipId === 'null') {
      membershipId = this.getMembershipId();
      if (!membershipId || membershipId === 'null') {
        // retry will occur but we are lacking product info on cold load
        return [];
      }
    }
    field.config.payload.isLoading = true;
    this.l360ApiService.getIdentificationTypes(membershipId.toString()).subscribe((response: any) => {
      if (response?.data) {
        const dropdownValues = response.data;
        if (!dropdownValues) {
          return;
        }

        const fieldsTypeConfig = response.data;
        const list = Array.from(new Set(dropdownValues)).map((item: any) => buildEnumItem(item.id, item.name));
        const lstFieldsTypeConfig = Array.from(new Set(fieldsTypeConfig)).map((item: any) => buildEnumItem(item.id, item.fieldsTypeConfig));

        field.config.payload['dropdownLists'] = JSON.stringify(list);
        field.config.payload['fieldsTypeConfig'] = JSON.stringify(lstFieldsTypeConfig);
        field.config.payload.isLoading = false;
        this.onChangeIdentificationTypes(context);
        return list;
      }
    });

    return [];
  };

  public onChangeIdentificationTypes = (context: LogicFnContext): void => {
    try {
      const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
      let idType;
      if (membershipTypeName?.toLowerCase() === 'business') {
        idType = this.statefulService.wrap(context.field.config.storageId, null, context.field.pageIterationId).value;
      } else {
        idType = this.statefulService.wrap(context.field.config.storageId).value;
      }

      const objIdentificationChildFields: PersonalDynamic = JSON.parse(JSON.stringify(UIFieldMapping)) as PersonalDynamic;

      if (context.field.config.payload.fieldsTypeConfig && (context.field.config.payload.fieldsTypeConfig as any).length > 0) {
        const lstFieldsTypeConfig = context.field.config.payload['fieldsTypeConfig']
          ? (JSON.parse(context.field.config.payload['fieldsTypeConfig'] as string) as ValueLabelItem[])
          : [];

        this.setIdentificationTypes(context, lstFieldsTypeConfig, idType, objIdentificationChildFields);
      }
    } catch (e) {}
  };

  /**
   * Set IdentificationTypes
   * @param context page and iteration info
   * @param lstFieldsTypeConfig value holds the FieldsTypeConfig
   * @param idType value holds type of ID type
   * @param objIdentChildFields value holds Identification Child Fields
   */
  private readonly setIdentificationTypes = (
    context: LogicFnContext,
    lstFieldsTypeConfig: any,
    idType: any,
    objIdentChildFields: any
  ): void => {
    if (lstFieldsTypeConfig?.filter((t) => t.value === idType).length > 0) {
      const objSelectedTypeFields = lstFieldsTypeConfig.filter((t) => t.value === idType)[0].label as string;

      const fieldArrays = objSelectedTypeFields.split('|') as any[];
      fieldArrays.shift();

      if (!fieldArrays || fieldArrays.length === 0) {
        return;
      }

      fieldArrays.forEach((element) => {
        const uiFields = element.split(':') as any[];
        if (uiFields[0] !== 'IssuedByType') {
          if (objIdentChildFields[uiFields[0]]) {
            objIdentChildFields[uiFields[0]].isRequired = /true/i.test((uiFields[1] as string).toLowerCase());
          }
        } else if (uiFields[0] === 'IssuedByType' && objIdentChildFields.IssuedBy.isRequired) {
          switch (uiFields[1]) {
            case 'FreeText':
              objIdentChildFields.IssuingInstitution.isRequired = true;
              break;
            case 'Country':
              objIdentChildFields.IssuingCountry.isRequired = true;
              break;
            case 'State':
              objIdentChildFields.IssuingState.isRequired = true;
          }
        }
      });
      const identificationFieldsTypeConfigStorageId = context.field.config.storageId.replace('/idType', '/identificationFieldsTypeConfig');

      const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
      if (membershipTypeName?.toLowerCase() === 'business') {
        this.statefulService.wrap(identificationFieldsTypeConfigStorageId, null, context.field.pageIterationId).value =
          JSON.stringify(objIdentChildFields);
      } else {
        this.statefulService.wrap(identificationFieldsTypeConfigStorageId).value = JSON.stringify(objIdentChildFields);
      }
    } else {
      this.statefulService.wrap(context.field.config.storageId).value = null;
    }
  };

  /**
   *  Triggering  Validation on Loaad
   *    * @param context - context of the field
   */
  public onReValidateField = (context: LogicFnContext): void => {
    if (context.field.data.value) {
      context.field.dirty = true;
    }
  };

  /**
   *  Set Youth Membership age validation on load
   *    * @param context - context of the field
   */
  public onReValidateAgeField = (context: LogicFnContext, defaultMinAge: number, defaultMaxAge: number): void => {
    const isYouth = this.statefulService.wrap(OAO_MEMBERSHIP_IS_YOUTH_STORAGE_ID).value;

    /** If youth membership & Is Admin Tool flag is enabled then fecth Min & Max age from Admin Portal Db by calling API */
    if (isYouth) {
      const membershipId = this.getMembershipId();
      this.l360ApiService.getMemberships().subscribe((response: any) => {
        if (response?.data?.memberships) {
          const memberships = response.data.memberships.filter(
            (membership) => membership.membershipID.toString() === membershipId.toString()
          );
          memberships.forEach((element) => {
            this.statefulService.wrap(OAO_MEMBERSHIP_MIN_AGE_STORAGE_ID).value = element.minAge;
            this.statefulService.wrap(OAO_MEMBERSHIP_MAX_AGE_STORAGE_ID).value = element.maxAge;
          });
        }
      });
    }

    if (context.field.data.value) {
      context.field.dirty = true;
    }
  };

  /**
   * Load OAO Employment Status
   * @param targetFieldId - ID of Enum
   */
  public oaoEmploymentStatusEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const field = context.field;
    const membershipId = this.getMembershipId();
    if (!field.config.payload) field.config.payload = {};

    if (field.config.payload.membershipId != membershipId) field.config.payload = { membershipId };

    if (
      field.config.payload.isLoading ||
      (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0)
    ) {
      const dropdownValues = field.config.payload['dropdownLists']
        ? (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
        : [];
      return dropdownValues;
    }
    field.config.payload.isLoading = true;
    this.l360ApiService.getEmploymentStatus(membershipId.toString()).subscribe((response: any) => {
      if (response?.data) {
        const dropdownValues = response.data;
        if (!dropdownValues) {
          return;
        }
        const list = Array.from(new Set(dropdownValues)).map((item: any) => buildEnumItem(item.name, item.name));
        const empStatusList = this.sortEnumList(list);
        field.config.payload = { ...field.config.payload, dropdownLists: JSON.stringify(empStatusList), isLoading: false };
        // setting value to html element after enum loaded from API
        this.setValueToHTMLElement(field, empStatusList);
        return empStatusList;
      }
    });

    return [];
  };

  /**
   * Load OAO Disclosure Content
   * @param pageId
   */
  public loadOAODisclosure = (_context: LogicFnContext): Promise<any> => {
    return new Promise<boolean>((resolve) => {
      if (_context.field.id === 'oao-application-review-disclosure') {
        this.l360ApiService.disclosureSub.subscribe((response) => {
          if (response?.length > 0) {
            this.handleResponseFromOAODisclosure(response, _context, resolve);
          }
        });
        this.l360ApiService.getDisclsoures();
      }
    });
  };

  private handleResponseFromOAODisclosure(response, _context, resolve) {
    let disclosure: Disclosure[] = [];
    let applicants: DisclosureApplicant[] = [];
    this.bundleHelperService.refreshBundle(() => {
      // Old data may not have category property. Filtering only disclosure category may endup in some old get filtered out
      // So returning all disclosures except crossSell and SmartSign
      disclosure = response.filter(
        (t) =>
          t.category?.toLowerCase() !== DisclosureCategory.CorssSell.toLowerCase() &&
          t.category?.toLowerCase() !== DisclosureCategory.SmartSign.toLowerCase()
      );
      // On race condition, get bundle or put bundle overrides the disclosure object from bundle so below code will
      // repopulate the data if it is overriden
      // Remove beloe temp code once long term fix available
      // Temp code start
      let tryCount = 0;
      const bundleSize = this.statefulService.arraySize('api:oaoMemberInformation/oaoDisclosure');
      const poll = () => {
        const size = this.statefulService.arraySize('api:oaoMemberInformation/oaoDisclosure');
        if (size < 1 && tryCount < 3) {
          tryCount++;
          this.l360ApiService.getFinalReviewDisclsoure().subscribe(() => {
            this.bundleHelperService.refreshBundle(() => {
              poll();
            });
          });
        }
      };
      if (this.l360ApiService.getIsDisclosureLoaded && bundleSize < 1 && disclosure.length > 1) {
        poll();
      }
      // Temp code End
      const lenderName = this.lenderService.lenderDataInfo?.lenderName;
      disclosure.forEach((item, i) => {
        applicants = [];
        item.content = item.content.replace(/{lenderName}/gi, lenderName);
        item.storageId = `api:oaoMemberInformation/oaoDisclosure/${i}/accepted`;
        item.agreedDateStorageId = `api:oaoMemberInformation/oaoDisclosure/${i}/disclosureAgreeDate`;
        this.setApplicants(item, item.isPDFFile || item.isLinkType, applicants, i);
      });
      this.statefulService.wrap('mem:hasDisclosure').value = !disclosure.length;
      _context.field.config.content = disclosure.length ? JSON.stringify(disclosure) : '';
      resolve(true);
    });
  }

  private setValueToHTMLElement(field: FieldInstance, enumList: ValueLabelItem[]) {
    // SSR-safe: do not access document on the server
    if (!this.isBrowser) return;
    const value = field.data.valueString;
    const dropDownList = document.getElementById(field.id) as HTMLSelectElement;
    if (dropDownList) {
      const selectedValue = enumList.filter((f) => f.value.toString() === value);
      if (selectedValue?.length > 0) {
        dropDownList.value = value;
      }
    }
  }

  private sortEnumList(list: ValueLabelItem[]): ValueLabelItem[] {
    return list.sort((n1, n2) => {
      if (n1.value > n2.value) {
        return 1;
      }

      if (n1.value < n2.value) {
        return -1;
      }

      return 0;
    });
  }

  /**
   * Set Applicants for Disclosures
   * @param item
   * @param checkAllowToAccept
   * @param applicants
   * @param i
   */
  private setApplicants(item: Disclosure, checkAllowToAccept: boolean, applicants: DisclosureApplicant[], i: number): void {
    let allowToAccept = false;
    const protocol = 'api:';
    const storagePath = 'oaoMemberInformation/oaoDisclosure';
    item.applicants.forEach((value, index) => {
      if (value.applicant) {
        const currentRequestBodyInSS = this.statefulService.wrap('ss:current-patch-request-body').valueString;
        const isAcceptBundleValue = this.statefulService.wrap(protocol + storagePath + `/${i}/applicants/${index}/isAccept`).value;
        const isFinalReviewDisclosureAgreed = this.statefulService.wrap(
          protocol + 'oaoMemberInformation/isFinalReviewDisclosureAgreed'
        ).value;
        const isAccept = this.customFunctionsService.determineIsAcceptValue(
          storagePath + `/${i}/applicants/${index}/isAccept`,
          currentRequestBodyInSS,
          isAcceptBundleValue,
          isFinalReviewDisclosureAgreed
        );
        if (isAccept && checkAllowToAccept) {
          allowToAccept = true;
        }
        applicants.push({
          accepted: isAccept,
          storageId: protocol + storagePath + `/${i}/applicants/${index}/isAccept`,
          agreedDateStorageId: protocol + storagePath + `/${i}/applicants/${index}/acceptDateTime`,
          label: `${
            item.placeHolderAcceptText
              ? item.placeHolderAcceptText.replace('$Applicant Name$', value.applicant)
              : `I, ${value.applicant}, authorize this credit check.`
          }`,
          applicant: `${value.applicant}`
        });
      }
    });
    item.applicants = applicants;
    item.allowToAccept = checkAllowToAccept ? allowToAccept : true;
  }

  private readonly getStartAndEndDates = (dates) => {
    dates = this.sortDates(dates, 'desc');
    const nonSingleEntryDates = dates.filter((t) => !t.isSingleEntry);
    let startDate = nonSingleEntryDates[0].start;
    let endDate = nonSingleEntryDates[0].end;
    const filteredDates = dates.filter(
      (t) => (t.start <= startDate && t.end >= endDate) || (t.start <= startDate && t.end <= endDate && t.end >= startDate)
    );

    if (filteredDates?.length > 1) {
      const startDates = filteredDates.map((t) => t.start).sort((n1, n2) => n1 - n2);
      const endDates = filteredDates.map((t) => t.end).sort((n1, n2) => n2 - n1);
      dates = this.notMatched(dates, filteredDates);
      startDate = startDates[0];
      endDate = endDates[0];

      dates.push({ start: startDate, end: endDate, isProcessed: true });
    } else {
      nonSingleEntryDates[0].isSingleEntry = true;
      nonSingleEntryDates[0].isProcessed = true;
    }

    if (dates?.filter((t) => !t.isProcessed).length > 0) {
      dates = this.getStartAndEndDates(dates);
    }
    return dates;
  };

  private readonly notMatched = (arr1, arr2) => {
    const ret = [];
    for (const i in arr1) {
      if (arr2.indexOf(arr1[i]) == -1) {
        ret.push(arr1[i]);
      }
    }
    return ret;
  };

  private readonly sortDates = (dates, sortType = 'asc', sortBy = 'start') => {
    return dates.sort((a, b) => {
      if (sortType == 'asc') {
        return a[sortBy] - b[sortBy];
      }

      return b[sortBy] - a[sortBy];
    });
  };

  public updateProperty = (_context: LogicFnContext, apiRoot: string, storageId: string, value: PrimitiveType) => {
    setTimeout(() => {
      const cardSize = this.statefulService.arraySize(`${apiRoot}`, _context.field.pageIterationId);
      for (let i = cardSize; i > 0; i--) {
        this.statefulService.wrap(storageId, i - 1, _context.page.pageIterationId).value = value;
      }
    });
  };

  /**
   * Method set a property value to most recent record.
   * @param _context field context
   * @param apiRoot root object of the property
   * @param storageId storage id to setvalue
   * @param value value to update
   */
  public updateMostRecentProperty = (_context: LogicFnContext, apiRoot: string, storageId: string, value: PrimitiveType) => {
    const pageIterationId = getPageIterationId(_context);
    setTimeout(() => {
      const cardSize = this.statefulService.arraySize(`${apiRoot}`, pageIterationId);
      if (cardSize > 0) {
        this.statefulService.wrap(storageId, cardSize - 1, pageIterationId).value = value;
      }
    });
  };

  /**
   * OAO ONLY
   * Remove additional co-applicant if co-app is empty
   */
  public onChangeAdditionalCoApp = (context: LogicFnContext): void => {
    if (!this.visibleFunctionsService.visibleIsOAOPrefillEnabled(context)) {
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillOption').value = 'Manual';
      this.statefulService.wrap('mem:primary-application-status-co-applicant').value = true;
    } else {
      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillOption').value = null;
      this.statefulService.wrap('mem:primary-application-status-co-applicant').value = null;
    }
    this.l360ApiService.clearDisclosure();
  };

  public onChangeAdditionalJointCoApp = (context: LogicFnContext): void => {
    if (!this.visibleFunctionsService.visibleIsOAOPrefillEnabled(context)) {
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/autoFillOption').value = 'Manual';
      this.statefulService.wrap('mem:primary-application-status-additional-co-app').value = true;
    } else {
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/autoFillOption').value = null;
      this.statefulService.wrap('mem:primary-application-status-additional-co-app').value = null;
    }

    // Grab the additional co-applicant first name to determine if we need to remove the additional co-applicant
    const hasAdditionalCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/firstName').valueString ? true : false;
    // Only remove the additional co-applicant if the first name is empty
    if (hasAdditionalCoApplicant) {
      this.oaoPageHelper.deleteOaoApplicant('api:oaoMemberInformation/additionalCoApplicant');
    }
    this.l360ApiService.clearDisclosure();
  };

  /**
   * Load Responsible list for business
   *   * @param arrayPath - Path of respective business array
   */
  /** To load Responsible,Additional Reponsible and Authorized card List */
  public onChangeResponsiblePartiesDDL = (context: LogicFnContext, arrayPath: string): void => {
    const arrayCount = this.statefulService.arraySize(arrayPath);
    if (
      arrayCount > 0 &&
      (!this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_FIRSTNAME).valueStringOnly ||
        !this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_EMAIL).valueStringOnly)
    ) {
      this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_FIRSTNAME).value =
        this.statefulService.wrap(OAO_PRIMARY_APPLICANT_FIRSTNAME).value;
      this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_LASTNAME).value =
        this.statefulService.wrap(OAO_PRIMARY_APPLICANT_LASTNAME).value;
      this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_MOBILENUMBER).value =
        this.statefulService.wrap(OAO_PRIMARY_APPLICANT_MOBILENUMBER).value;
      this.statefulService.wrap(OAO__BUSSINESS_APP_RESPONSIBE_PARTY_ONE_EMAIL).value =
        this.statefulService.wrap(OAO_PRIMARY_APPLICANT_EMAIL).value;
    }
  };

  /**
   * Load OAO Housing type
   * @param targetFieldId - ID of Enum
   */
  public oaoHousingTypeEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const field = context.field;

    if (!field.config.payload) {
      field.config.payload = {};
    }

    if (
      field.config.payload.isLoading ||
      (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0)
    ) {
      const dropdownValues = field.config.payload['dropdownLists']
        ? (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
        : [];
      return dropdownValues;
    }

    field.config.payload.isLoading = true;

    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    const logSrc = 'workflow';
    this.logEvent('ecp.ui.picklist', {
      eventState: 'start',
      correlationId: eventPairId,
      source: logSrc,
      keys: 'OwnershipCode'
    });

    const picklistTypes = this.l360ApiService.getPickListTypes('OwnershipCode');
    if (picklistTypes?.length > 0) {
      const dropdownValues = picklistTypes[0].pickListItems;
      if (!dropdownValues || dropdownValues.length <= 0) {
        return;
      }
      const list = Array.from(new Set(dropdownValues)).map((item: any) => buildEnumItem(item.id, item.value));
      field.config.payload['dropdownLists'] = JSON.stringify(list);
      field.config.payload.isLoading = false;

      this.logEvent('ecp.ui.picklist', {
        eventState: 'end',
        correlationId: eventPairId,
        source: logSrc,
        keys: 'OwnershipCode'
      });

      return list;
    }
    return [];
  };

  public loadSlideoutContent = (): Promise<SlideOutContent> => {
    return new Promise<SlideOutContent>((resolve) => {
      const content: SlideOutContent = {
        title: 'About Demographics',
        header: 'Why do we ask this?',
        description: `<b> The purpose of collecting this information </b> is to help ensure that all applicants are treated fairly and that
        the housing needs of communities and neighborhoods are being fulfilled. For residential mortgage lending, Federal law requires
        that we ask applicants for their demographic information (ethnicity, race, and sex) in order to monitor our compliance with
        equal credit opportunity, fair housing, and home mortgage disclosure laws. You are not required to provide this information,
        but are encouraged to do so. You may select one or more designations for "Ethnicity" and one or more designations for "Race".
        <b> The law provides that we may not discriminate </b> on the basis of this information, or on whether you choose to provide it.
        If you do not wish to provide some or all of this information, select “No, decline to provide.”`
      };
      resolve(content);
    });
  };

  /**
   * Load OAO memerships from enum builder
   * @param targetFieldId - ID of Enum
   */
  public oaoMembershipsEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const field = context.field;
    if (!field.config.payload) {
      field.config.payload = {};
    }
    if (
      field.config.payload.isLoading ||
      (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0)
    ) {
      const dropdownValues = field.config.payload['dropdownLists']
        ? (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
        : [];
      return dropdownValues;
    }

    field.config.payload.isLoading = true;
    this.l360ApiService.getMemberships().subscribe((response: any) => {
      const memberships = response?.data;
      if (!memberships) {
        return;
      }

      const dropdownValues = response.data.memberships.filter((membership) =>
        ['personal', 'business'].includes(membership.membershipTypeName.toString().toLowerCase())
      );

      const organizationID = response.data.organizationID ? response.data.organizationID : 0;
      this.statefulService.wrap('api:oaoMemberInformation/membership/organizationID').value = Number(organizationID);

      const membershipsList: any[] = [];
      dropdownValues.forEach((element) => {
        const enumvalue = {
          enumId: element.membershipTypeName,
          icon: element.isYouth ? 'Youth' : element.membershipTypeName,
          isYouth: element.isYouth,
          label: element.membershipName,
          maxAge: element.maxAge,
          description: element.description,
          isEnabledChecklist: element.isEnabledChecklist,
          checklistDescription: element.checklistDescription,
          membershipFee: element.membershipFee,
          minAge: element.minAge,
          value: element.membershipID?.toString(),
          virtual: false,
          isPromoCode: element.maxPromoCodeDisplay > 0,
          organizationID,
          redirectURL: element.redirectURL,
          isHideMembershipSelection: element.isHideMembershipSelection
        };
        membershipsList.push(enumvalue);
      });

      membershipsList.sort((a, b) => a.label.localeCompare(b.label));

      field.config.payload['dropdownLists'] = JSON.stringify(membershipsList?.filter((mem) => mem.isHideMembershipSelection !== true));
      field.config.payload.isLoading = false;
      return membershipsList;
    });

    return [];
  };

  /* Get MembershipId */
  private getMembershipId(): string {
    const bundleMembershipId = this.statefulService.wrap(OAO_MEMBERSHIPID_STORAGE_ID).value;
    const dropdownMembershipId = this.statefulService.wrap(OAO_DROPDOWN_MEMBERSHIPID_STORAGE_ID).value;
    if (!bundleMembershipId && !dropdownMembershipId) {
      const memMembershipId = this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipID').value;
      const memdropdownMembershipId = this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value;
      const tempMembershipId = memMembershipId || memdropdownMembershipId;
      if (!tempMembershipId) {
        return 'null';
      }
      return tempMembershipId.toString();
    }
    const membershipId = bundleMembershipId ? bundleMembershipId.toString() : dropdownMembershipId.toString();
    return membershipId;
  }
}
